// LLM response parsing (ARCHITECTURE.md §11.3 step 3).
//
// LLMs are unreliable serializers: responses may be wrapped in markdown fences,
// padded with prose, or partially malformed. This module extracts the JSON
// payload, validates it against the CandidateProposal schema, and degrades
// gracefully — returning the proposals that *do* validate and silently dropping
// the rest, rather than throwing. A completely unparseable response yields [].

import {
  CandidateProposalArraySchema,
  CandidateProposalSchema,
  type CandidateProposal,
} from "./types.js";

/**
 * Pull the most plausible JSON value out of a raw LLM response. Strips ```json
 * fences, then falls back to the first `[`…`]` or `{`…`}` span. Returns the
 * candidate substring, or `undefined` when nothing JSON-like is present.
 */
function extractJsonText(response: string): string | undefined {
  const trimmed = response.trim();
  if (trimmed.length === 0) return undefined;

  // Prefer a fenced code block when present.
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const body = (fence?.[1] ?? trimmed).trim();

  // Whichever bracket appears first determines the top-level shape: a leading
  // `[` is an array, a leading `{` is a single object. (An object may itself
  // contain arrays, e.g. `evidencePoints: []`, so we must not blindly grab the
  // first `[` in the string.)
  const arrayStart = body.indexOf("[");
  const objStart = body.indexOf("{");

  const arrayFirst =
    arrayStart !== -1 && (objStart === -1 || arrayStart < objStart);
  if (arrayFirst) {
    const arrayEnd = body.lastIndexOf("]");
    if (arrayEnd > arrayStart) return body.slice(arrayStart, arrayEnd + 1);
  }

  if (objStart !== -1) {
    const objEnd = body.lastIndexOf("}");
    if (objEnd > objStart) return body.slice(objStart, objEnd + 1);
  }

  return undefined;
}

/**
 * Parse an LLM response into validated CandidateProposals.
 *
 * Accepts either a JSON array or a single JSON object. Each element is validated
 * independently: valid proposals are kept, invalid ones are dropped. Malformed
 * JSON or a non-array/object payload yields an empty array.
 */
export function parseResponse(response: string): CandidateProposal[] {
  const jsonText = extractJsonText(response);
  if (jsonText === undefined) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonText);
  } catch {
    return [];
  }

  // Fast path: the whole payload validates as an array.
  const asArray = CandidateProposalArraySchema.safeParse(parsed);
  if (asArray.success) return asArray.data;

  // Otherwise validate element-by-element, dropping malformed entries.
  const items = Array.isArray(parsed) ? parsed : [parsed];
  const proposals: CandidateProposal[] = [];
  for (const item of items) {
    const result = CandidateProposalSchema.safeParse(item);
    if (result.success) proposals.push(result.data);
  }
  return proposals;
}
